import { cn } from 'cn';
import * as React from 'react';

type ScrollableRegionProps = Omit<
  React.ComponentProps<'div'>,
  'aria-label' | 'role' | 'tabIndex'
> & {
  'aria-label': string;
};

/**
 * A horizontally scrollable container that keyboard users can actually reach.
 *
 * tabIndex makes the overflow scrollable without a pointer; the region role
 * and its required label tell assistive technology what was entered. The MUI
 * tables this replaces provide neither.
 */
function ScrollableRegion({ 'aria-label': ariaLabel, className, ...props }: ScrollableRegionProps) {
  // tw-shadcn has no logger dependency; keep the missing-name guard as console.warn.
  if (process.env.NODE_ENV !== 'production' && !ariaLabel?.trim()) {
    console.warn(
      'ScrollableRegion renders without an accessible name. Give it a non-empty aria-label.',
    );
  }

  return (
    <div
      data-slot="scrollable-region"
      className={cn('overflow-x-auto outline-none focus-visible:focus-ring', className)}
      {...props}
      role="region"
      tabIndex={0}
      aria-label={ariaLabel}
    />
  );
}

export { ScrollableRegion };
